<template>
  <div class="max-w-lg w-full mx-auto">
    <div class="flex flex-wrap -mx-2 space-y-4 md:space-y-0">
      <div class="mt-8 mx-auto max-w-7xl px-4 sm:mt-4 sm:px-6">
        <div class="text-center">
          <h1
            class="text-2xl tracking-tight font-black text-gray-900 sm:text-3xl"
          >
            <span class="block text-mygrad">Let's get started</span>
          </h1>
        </div>
      </div>
    </div>
    <ValidationObserver v-slot="{ handleSubmit }">
      <form
        class="space-y-4 text-gray-700 mt-4"
        @submit.prevent="handleSubmit(submit)"
      >
        <div class="flex flex-wrap -mx-2 space-y-4 md:space-y-0">
          <div class="w-full px-2">
            <ValidationProvider
              rules="required|min:3"
              v-slot="{ errors, valid }"
            >
              <label class="block mb-1" for="formGridCode_name"
                >Full name</label
              >
              <input
                class="w-full h-10 px-3 text-base placeholder-gray-600 border rounded-lg focus:shadow-outline"
                type="text"
                id="formGridCode_name"
                name="name"
                v-model="form.name"
                placeholder="Your Name to be shown at graduation"
                v-bind:class="{
                  'border-red-700 text-red-900': errors.length > 0,
                  'border-green-700': valid,
                }"
              />
            </ValidationProvider>
          </div>
        </div>
        <div class="flex flex-wrap -mx-2 space-y-4 md:space-y-0">
          <div class="w-full px-2">
            <ValidationProvider
              name="Email Address"
              rules="required|email|personal_email"
              v-slot="{ valid, errors }"
              vid="email"
            >
              <label class="block mb-1" for="formGridCode_email"
                >Email Address</label
              >
              <input
                ref="email"
                class="w-full h-10 px-3 text-base placeholder-gray-600 border rounded-lg focus:shadow-outline"
                type="email"
                id="formGridCode_email"
                name="email"
                v-model="form.email"
                placeholder="john.doe@example.com"
                v-bind:class="{
                  'border-red-700 text-red-900': errors.length > 0,
                  'border-green-700': valid,
                }"
              /><span>{{ errors[0] }}</span>
            </ValidationProvider>
          </div>
        </div>
        <div class="flex flex-wrap -mx-2 space-y-4 md:space-y-0">
          <div class="w-full px-2">
            <ValidationProvider
              name="Confirmation Email Address"
              rules="required|confirmed:email"
              v-slot="{ valid, errors }"
            >
              <label class="block mb-1" for="formGridCode_email_conf"
                >Confirm Email Address</label
              >
              <input
                class="w-full h-10 px-3 text-base placeholder-gray-600 border rounded-lg focus:shadow-outline"
                type="email"
                id="formGridCode_email_conf"
                name="confirmed_email"
                v-model="form.confirmEmail"
                placeholder="john.doe@example.com"
                v-bind:class="{
                  'border-red-700 text-red-900': errors.length > 0,
                  'border-green-700': valid,
                }"
              /><span>{{ errors[0] }}</span>
            </ValidationProvider>
          </div>
        </div>

        <!-- TODO ADD dates and list faculties -->
        <div class="flex flex-wrap -mx-2 space-y-4 md:space-y-0">
          <div class="w-full px-2">
            <label class="block mb-1" for="formGridCode_event">Event</label>
            <select
              v-model="form.event"
              @change="$emit('graduation-date-change', form.event)"
              class="w-full h-10 px-3 text-base placeholder-gray-600 border rounded-lg focus:shadow-outline"
              id="formGridCode_event"
              name="event"
            >
              <option disabled :value="null">Select your event date</option>

              <option
                v-for="ceremony in events"
                :value="ceremony['id']"
                :key="ceremony['id']"
              >
                {{ formatCeremonyOption(ceremony) }}
              </option>
            </select>
          </div>
        </div>
        <div v-if="surveyTitle || surveyDescription" class="flex flex-wrap -mx-2">
          <div class="w-full rounded-lg border border-gray-200 bg-gray-50 p-4 mx-2">
            <h2 v-if="surveyTitle" class="text-xl font-extrabold text-primary">{{ surveyTitle }}</h2>
            <p v-if="surveyDescription" class="mt-1 text-sm leading-6 text-gray-700">{{ surveyDescription }}</p>
          </div>
        </div>
        <div
          v-for="(field, index) in applyConditions()"
          :index="index"
          :key="field.name"
          class="flex flex-wrap -mx-2 space-y-4 md:space-y-0"
        >
          <div class="w-full px-2">
            <ValidationProvider :rules="field.rules" v-slot="{ valid, errors }">
              <label
                v-if="field.titleFormat == 'markdown'"
                class="block mb-1 markdownHtmlCustom"
                style=""
                :for="field.name"
                v-html="markdownHtml(field.title)"
              ></label>
              <label
                class="block mb-1"
                style="white-space: pre-wrap"
                :for="field.name"
                v-else
                >{{ field.title }}</label
              >
              <div v-if="field.type == 'radio'">
                <div v-for="(choice, c_index) in field.choices" :key="c_index">
                  <label :for="choice.value">{{ choice.text }} </label>

                  <input
                    type="radio"
                    v-model="getField(field).value"
                    :id="choice.value"
                    :name="field.name"
                    :value="choice.value"
                    :disabled="!field.enabled"
                  />
                </div>
              </div>
              <div
                v-else-if="field.type === 'markdown'"
                class="markdownHtmlCustom"
                v-html="markdownHtml(field.content)"
              ></div>
              <div v-else-if="field.type == 'select'">
                <select
                  :id="field.name"
                  :name="field.name"
                  class="w-full h-10 px-3 text-base placeholder-gray-600 border rounded-lg focus:shadow-outline"
                  v-model="getField(field).value"
                  :disabled="!field.enabled"
                  :ref="field.name"
                >
                  <option selected disabled :value="null">
                    {{ field.default }}
                  </option>

                  <option
                    v-for="(choice, c_index) in field.choices"
                    :key="c_index"
                    :value="choice.value"
                    :data-unique="
                      'unique' in choice ? choice.unique : choice.value
                    "
                  >
                    {{ choice.text }}
                  </option>
                </select>
              </div>
              <div v-else-if="field.type === 'multi-select'" class="space-y-2">
                <label v-for="(choice, c_index) in field.choices" :key="c_index" :for="`${field.name}-${c_index}`" class="flex items-center gap-2 text-sm">
                  <input type="checkbox" v-model="getField(field).value" :id="`${field.name}-${c_index}`" :name="field.name" :value="choice.value" :disabled="!field.enabled" />
                  <span>{{ choice.text }}</span>
                </label>
              </div>
              <div v-else-if="field.type === 'presentationOnly'"></div>
              <input
                v-else
                class="w-full h-10 px-3 text-base placeholder-gray-600 border rounded-lg focus:shadow-outline"
                :type="field.type"
                :id="field.name"
                :name="field.name"
                :placeholder="field.placeholder"
                v-model="getField(field).value"
                :disabled="!field.enabled"
                v-bind:class="{
                  'border-red-700 text-red-900': errors.length > 0,
                  'border-green-700': valid,
                }"
              />
            </ValidationProvider>
          </div>
        </div>

        <button
          type="submit"
          class="inline-flex items-center justify-center w-full px-4 py-2 border border-blue-300 shadow-sm text-sm font-medium rounded-md text-white bg-blue-500 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
        >
          Go to Checkout
        </button>
      </form>
    </ValidationObserver>
  </div>
</template>

<script>
import marked from "marked";
import { extend } from "vee-validate";
import structuredClone from "@ungap/structured-clone";
import { applySurveyConditions } from "../../surveys/conditions.js";
import {
  required,
  min,
  max,
  email,
  regex,
  confirmed,
} from "vee-validate/dist/rules";

extend("personal_email", {
  message: (field) =>
    "The " +
    field +
    " value is not a personal email, please enter an email not ending in ac.uk.",
  validate: (value) => !/^.*(\.ac\.uk)$/.test(value),
});

extend("required", {
  ...required,
  message: "This field is required",
});
extend("min", min);
extend("confirmed", {
  ...confirmed,
  message: "Your email addresses do not match",
});
extend("regex", regex);
extend("max", max);
extend("email", email);
extend("past_date", {
  validate: (value) => {
    if (!value) {
      return false;
    }
    return new Date(value) <= new Date();
  },
  message: "Date must be in the past",
});
export default {
  name: "Step1",
  props: {
    events: Array,
    additionalFieldsInput: Array,
    surveyTitle: { type: String, default: "" },
    surveyDescription: { type: String, default: "" },
  },
  data: function () {
    return {
      form: {
        name: "",
        event: null,
        faculty: null,
        additionalFields: [],
      },
    };
  },
  created: function () {
    this.initializeAdditionalFields(this.additionalFieldsInput);
    this.form.additionalFields.map((item) => {
      if (item.regex != null) {
        item.regex = new RegExp(item.regex);
      }
    });
  },
  watch: {
    additionalFieldsInput: {
      deep: true,
      handler(fields) {
        this.initializeAdditionalFields(fields);
      },
    },
  },
  methods: {
    initializeAdditionalFields(fields) {
      const currentValues = new Map(
        this.form.additionalFields.map((field) => [field.name, field.value])
      );
      this.form.additionalFields = (fields || []).map((field) => {
        const copy = structuredClone(field);
        if (currentValues.has(field.name)) copy.value = currentValues.get(field.name);
        else if (!("value" in copy)) copy.value = field.type === "multi-select" ? [] : null;
        if (copy.regex != null) copy.regex = new RegExp(copy.regex);
        return copy;
      });
    },
    submit() {
      console.log(this.form);
      this.$emit("submit", this.form);
    },
    getRulesObject(field) {
      return field.rules;
    },
    getField(field) {
      return this.form.additionalFields.filter((f) => f.name == field.name)[0];
    },
    applyConditions() {
      return applySurveyConditions(
        this.additionalFieldsInput,
        this.form.additionalFields,
        this.form,
      );
    },
    markdownHtml(value) {
      if (!value) return "";
      return marked.parse(value);
    },
    formatCeremonyOption(ceremony) {
      if (!ceremony) return "";
      const formattedDate = this.formatDate(ceremony.datetime);
      return ceremony.label
        ? `${formattedDate} - ${ceremony.label}`
        : formattedDate;
    },
    formatDate(dateString) {
      if (!dateString) return "";
      return new Date(dateString).toLocaleDateString("en-GB", {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      });
    },
  },
};
</script>
<style scoped>
.markdownHtmlCustom a {
  text-decoration: underline !important;
}
</style>
